// PantallaRegistroPacienteOnline.js

import React, { useState, useRef, useEffect, useContext, useMemo } from "react";
import {
  Container,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Stack,
} from "@mui/material";
import { WhatsApp, Facebook } from "@mui/icons-material";
import {
  styled,
  useTheme,
  ThemeProvider,
  createTheme,
} from "@mui/material/styles";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";

import HomeIcon from "@mui/icons-material/Home";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ForumIcon from "@mui/icons-material/Forum";
import ScienceIcon from "@mui/icons-material/Science";
import Paso1InformacionBasica from "./Paso1InformacionBasica";
import InformacionSalud from "./InformacionSalud";
import Paso4ComposicionFisica from "./Paso4ComposicionFisica";
import Paso6HablameDeTi from "./Paso6HablameDeTi";
import Bienvenida from "./Bienvenida";
import { GlassPanel } from "./styledComponents";

import { patologias, gruposAlergiasList } from "./constants";
import contenidos from "./contenidos/contenidos.json";
import { crearPaciente } from "../../services/patientService"; // Importar el servicio

import Swal from "sweetalert2"; // Importar SweetAlert2
import { useNavigate } from "react-router-dom"; // Importar useNavigate para redirección
import { AuthContext } from "../../context/AuthContext"; // Importar AuthContext

import validator from "validator";

// IMPORTA EL THEME BASE QUE TIENES (para no perder configuraciones previas)
import baseTheme from "../../theme/theme";

// Iconos personalizados para los pasos
const stepIcons = {
  1: <HomeIcon />,
  2: <ContactMailIcon />,
  3: <FavoriteIcon />,
  4: <ScienceIcon />,
  5: <ForumIcon />,
};

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 28,
    [theme.breakpoints.down("sm")]: {
      top: 18,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 4,
    border: 0,
    backgroundColor: "rgba(148,163,184,0.2)",
    borderRadius: 999,
    boxShadow: "0 2px 8px rgba(15,23,42,0.08)",
    transition: "all 0.3s ease",
    [theme.breakpoints.down("sm")]: {
      height: 3,
    },
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    boxShadow: `0 3px 12px ${theme.palette.primary.main}40`,
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    boxShadow: `0 3px 12px ${theme.palette.primary.main}40`,
  },
}));

const StepIconRoot = styled("div")(({ theme, ownerState }) => ({
  backgroundColor: "rgba(148,163,184,0.15)",
  zIndex: 1,
  color: theme.palette.grey[600],
  width: 56,
  height: 56,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  border: "2px solid rgba(148,163,184,0.2)",
  boxShadow: "0 8px 24px rgba(15,23,42,0.1)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  backdropFilter: "blur(8px)",
  "& svg": {
    fontSize: "1.5rem",
    transition: "transform 0.3s ease",
  },
  ...(ownerState.active && {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    color: "#fff",
    border: `2px solid ${theme.palette.primary.main}`,
    boxShadow: `0 12px 32px ${theme.palette.primary.main}40, 0 0 0 4px ${theme.palette.primary.main}18`,
    transform: "scale(1.08)",
    "& svg": {
      transform: "scale(1.1)",
    },
  }),
  ...(ownerState.completed && {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    color: "#fff",
    border: `2px solid ${theme.palette.primary.main}`,
    boxShadow: `0 8px 24px ${theme.palette.primary.main}35`,
  }),
  [theme.breakpoints.down("sm")]: {
    width: 36,
    height: 36,
    "& svg": {
      fontSize: "0.95rem",
    },
  },
}));

function ColorlibStepIcon(props) {
  const { active, completed, icon } = props;
  return (
    <StepIconRoot ownerState={{ completed, active }}>
      {stepIcons[String(icon)]}
    </StepIconRoot>
  );
}

const PantallaRegistroPacienteOnline = () => {
  const theme = useTheme();
  const { login } = useContext(AuthContext);
  const [dataNutriologoSubdominio, setDataNutriologoSubdominio] =
    useState(null);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // A) CREAR UN TEMA PERSONALIZADO BASADO EN TU TEMA BASE
  //    Cuando color3 cambie, se recalcula el tema con useMemo
  const customTheme = useMemo(() => {
    const nutriologoColors = dataNutriologoSubdominio?.nutriologoInfo || {};
    const fallbackPalette = baseTheme?.palette?.primary || {};

    const primaryMain =
      nutriologoColors.color3 || fallbackPalette.main || "#4f74fe";
    const primaryLight =
      nutriologoColors.color2 || fallbackPalette.light || primaryMain;
    const primaryDark =
      nutriologoColors.color1 || fallbackPalette.dark || primaryMain;

    return createTheme({
      ...baseTheme,
      palette: {
        ...baseTheme.palette,
        primary: {
          ...baseTheme.palette.primary,
          main: primaryMain,
          light: primaryLight,
          dark: primaryDark,
        },
      },
    });
  }, [dataNutriologoSubdominio]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const hostname = window.location.hostname;
      const subdominio = hostname.split(".")[0];

      if (!subdominio) {
        throw new Error("No se pudo determinar el subdominio.");
      }

      const response = await fetch(
        `/api/nutriologo/subdomain/info?subdominio=${subdominio}`
      );

      if (!response.ok) {
        throw new Error("Error al obtener los datos del backend");
      }
      const result = await response.json();
      if (
        !result ||
        Object.keys(result).length === 0 ||
        result.nutriologoInfo === null
      ) {
        navigate("/404", { replace: true });
        throw new Error("Subdominio no encontrado.");
      }
      setDataNutriologoSubdominio(result);
    } catch (err) {
      navigate("/404", { replace: true });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const steps = [
    "Bienvenido",
    "Información básica",
    "Información de Salud",
    "Composición",
    "Háblame de ti",
  ];

  const [activeStep, setActiveStep] = useState(0);

  const [formValuesBienvenida, setFormValuesBienvenida] = useState({
    telefono: "",
    password: "",
    confirmPassword: "",
  });

  const handleChangeBienvenida = (name, value) => {
    setFormValuesBienvenida((prev) => ({ ...prev, [name]: value }));
  };

  const [formValuesBasic, setFormValuesBasic] = useState({
    nombrePaciente: "",
    apellidoPaciente: "",
    edad: "",
    email: "",
    pesoPaciente: "",
    estatura: "",
    fechaNacimiento: "",
    sexo: "",
    embarazoActivo: "",
    tiempoEmbarazo: "",
  });

  const [formValuesSalud, setFormValuesSalud] = useState({
    patologias: patologias.reduce((acc, patologia) => {
      acc[patologia.clave] = false;
      return acc;
    }, {}),
    gruposAlergias: gruposAlergiasList.reduce((acc, grupo) => {
      acc[grupo.clave] = false;
      return acc;
    }, {}),
    contenidos: contenidos.reduce((acc, contenido) => {
      const id = contenido.nombre || contenido.nombre;
      acc[id] = false;
      return acc;
    }, {}),
  });

  const [formValuesComposicion, setFormValuesComposicion] = useState({
    biometriaHematicaFecha: "",
    hemoglobina: "",

    perfilLipidicoFecha: "",
    trigliceridos: "",
    trigliceridosMedida: "",
    colesterolTotal: "",
    colesterolTotalMedida: "",
    colesterolLDL: "",
    colesterolLDLMedida: "",
    colesterolHDL: "",
    colesterolHDLMedida: "",
    colesterolVLDL: "",
    colesterolVLDLMedida: "",

    quimicaSanguineaFecha: "",
    glucosa: "",
    glucosaMedida: "",
    creatinina: "",
    creatininaMedida: "",
    albumina: "",
    albuminaMedida: "",
    urea: "",
    ureaMedida: "",
    acidoUrico: "",
    acidoUricoMedida: "",
    hierro: "",
    hierroMedida: "",
    glucosaPlasma: "",
    glucosaPlasmaMedida: "",

    examenGeneralOrinaFecha: "",
    glucosaOrina: "",
    glucosaOrinaMedida: "",
    proteinasOrina: "",
    proteinasOrinaMedida: "",
    hemoglobinaOrina: "",
    hemoglobinaOrinaMedida: "",
    urobilinogeno: "",
    urobilinogenoMedida: "",
    cuerposCetonicos: "",
    cuerposCetonicos: { cantidad: "", medida: "ausencia" },
    pigmentosBiliares: { cantidad: "", medida: "ausencia" },
    nitritos: { cantidad: "", medida: "ausencia" },

    indicesCorporalesFecha: "",
    circunferenciaCintura: "",
    circunferenciaCinturaMedida: "",
    porcentajeGrasa: "",
    porcentajeGrasaMedida: "",
    porcentajeAgua: "",
    porcentajeAguaMedida: "",
    porcentajeMusculo: "",
    porcentajeMusculoMedida: "",
    edadDigestion: "",
    edadDigestionMedida: "",
    grasaVisceral: "",
    grasaVisceralMedida: "",

    antropometriaFecha: "",
    brazo: "",
    brazoMedida: "",
    hombros: "",
    hombrosMedida: "",
    pectoralBusto: "",
    pectoralBustoMedida: "",
    abdomenCintura: "",
    abdomenCinturaMedida: "",
    caderaGluteos: "",
    caderaGluteosMedida: "",
    muslo: "",
    musloMedida: "",
    pantorrilla: "",
    pantorrillaMedida: "",

    plieguesCutaneosFecha: "",
    pectoral: "",
    pectoralMedida: "",
    tricipital: "",
    tricipitalMedida: "",
    bicipital: "",
    bicipitalMedida: "",
    subescapular: "",
    subescapularMedida: "",
    midaxilar: "",
    midaxilarMedida: "",
    suprailiaco: "",
    suprailiacoMedida: "",
    abdominal: "",
    abdominalMedida: "",
    musloMedio: "",
    musloMedioMedida: "",
    pantorrillaMedia: "",
    pantorrillaMediaMedida: "",
  });

  const [formValuesGlucosa, setFormValuesGlucosa] = useState({
    glucosaCualquierHora: "",
    glucosaCualquierHoraFecha: "",
    glucosaCualquierHoraMedida: "",
    glucosaAyuno: "",
    glucosaAyunoFecha: "",
    glucosaAyunoMedida: "",
    hemoglobinaGlucosilada: "",
    hemoglobinaGlucosiladaFecha: "",
    hemoglobinaGlucosiladaMedida: "",
    toleranciaGlucosa: "",
    toleranciaGlucosaFecha: "",
    toleranciaGlucosaMedida: "",
  });

  const [formValuesHablameDeTi, setFormValuesHablameDeTi] = useState({});

  useEffect(() => {
    if (
      dataNutriologoSubdominio &&
      dataNutriologoSubdominio.historiaClinicas &&
      dataNutriologoSubdominio.historiaClinicas.length > 0
    ) {
      const titulosPreguntas =
        dataNutriologoSubdominio.historiaClinicas[0].preguntas.reduce(
          (acc, pregunta) => {
            acc[pregunta.nombre] = "";
            return acc;
          },
          {}
        );
      setFormValuesHablameDeTi(titulosPreguntas);
    }
  }, [dataNutriologoSubdominio]);

  const [patologiaSearch, setPatologiaSearch] = useState("");
  const [contenidoSearch, setContenidoSearch] = useState("");

  const paso1Ref = useRef(null);
  const paso2Ref = useRef(null);
  const paso3Ref = useRef(null);

  const handleChangeBasic = (name, value) => {
    setFormValuesBasic((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeSaludPatologias = (updatedPatologias) => {
    setFormValuesSalud((prev) => ({ ...prev, patologias: updatedPatologias }));
  };

  const handleChangeSaludGruposAlergias = (updatedGruposAlergias) => {
    setFormValuesSalud((prev) => ({
      ...prev,
      gruposAlergias: updatedGruposAlergias,
    }));
  };

  const handleChangeSaludContenidos = (updatedContenidos) => {
    setFormValuesSalud((prev) => ({ ...prev, contenidos: updatedContenidos }));
  };

  const handleChangeComposicion = (name, value) => {
    setFormValuesComposicion((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeGlucosa = (name, value) => {
    setFormValuesGlucosa((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeHablameDeTi = (name, value) => {
    setFormValuesHablameDeTi((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  };

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (formValuesBienvenida.telefono.length !== 10) {
        Swal.fire(
          "Error",
          "El número de teléfono debe contener 10 dígitos",
          "error"
        );
        return;
      }
      if (
        formValuesBienvenida.password === "" ||
        formValuesBienvenida.password.length < 6
      ) {
        Swal.fire(
          "Error",
          "Ingresa una contraseña mayor a 6 caracteres",
          "error"
        );
        return;
      }

      if (
        formValuesBienvenida.password !== formValuesBienvenida.confirmPassword
      ) {
        Swal.fire("Error", "Las contraseñas no coinciden.", "error");
        return;
      }
    }

    if (activeStep === 1) {
      const trimmedEmail = formValuesBasic.email?.trim();
      if (trimmedEmail && !validator.isEmail(trimmedEmail)) {
        Swal.fire("Error", "Ingresa un correo electrónico válido", "error");
        return;
      }
      const isValid = paso1Ref.current?.validate();
      if (!isValid) return;
    }

    if (activeStep === 2) {
      const isValid = paso2Ref.current?.validate();
      if (!isValid) return;
    }

    if (activeStep === 3) {
      const isValid = paso3Ref.current?.validate();
      if (!isValid) return;
    }

    if (activeStep === 4) {
      const validationErrors = validatePaso6HablameDeTi();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setSnackbarMessage("Por favor, corrige los errores en el formulario.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
      setErrors({});
      return;
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validatePaso6HablameDeTi = () => {
    const newErrors = {};
    // Agrega tus validaciones si son necesarias
    return newErrors;
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Bienvenida
            dataNutriologoSubdominio={dataNutriologoSubdominio}
            formValues={formValuesBienvenida}
            onChange={handleChangeBienvenida}
            handleNext={handleNext}
          />
        );
      case 1:
        return (
          <Paso1InformacionBasica
            ref={paso1Ref}
            formValues={formValuesBasic}
            formValuesTelefono={formValuesBienvenida.telefono}
            onChange={handleChangeBasic}
          />
        );

      case 2:
        return (
          <InformacionSalud
            ref={paso2Ref}
            selectedPatologias={formValuesSalud.patologias}
            selectedGruposAlergias={formValuesSalud.gruposAlergias}
            onPatologiaChange={handleChangeSaludPatologias}
            onGrupoAlergiaChange={handleChangeSaludGruposAlergias}
            selectedContenidos={formValuesSalud.contenidos}
            onContenidoChange={handleChangeSaludContenidos}
            patologiaSearch={patologiaSearch}
            setPatologiaSearch={setPatologiaSearch}
            contenidoSearch={contenidoSearch}
            setContenidoSearch={setContenidoSearch}
          />
        );

      case 3:
        return (
          <Paso4ComposicionFisica
            ref={paso3Ref}
            formValues={formValuesComposicion}
            onChange={handleChangeComposicion}
            formValuesGlucosa={formValuesGlucosa}
            onChangeGlucosa={handleChangeGlucosa}
            formValuesBasic={formValuesBasic}
          />
        );

      case 4:
        return (
          <Paso6HablameDeTi
            formValues={formValuesHablameDeTi}
            onChange={handleChangeHablameDeTi}
            dataNutriologoSubdominio={dataNutriologoSubdominio}
            setDataNutriologoSubdominio={setDataNutriologoSubdominio}
            errors={errors}
          />
        );

      default:
        return null;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const datosPaciente = {
        formValuesBasic,
        formValuesBienvenida,
        formValuesSalud,
        formValuesComposicion,
        formValuesGlucosa,
        formValuesHablameDeTi,
        dataNutriologoSubdominio,
      };

      await crearPaciente(datosPaciente, patologias);

      Swal.fire("Éxito", "Paciente creado exitosamente.", "success").then(
        async () => {
          // Autenticar automáticamente al usuario
          const { telefono, password } = formValuesBienvenida;
          const idNutriologo =
            dataNutriologoSubdominio?.nutriologoInfo?._id ?? null;

          if (!idNutriologo) {
            Swal.fire(
              "Error",
              "No se encontró información del nutriólogo para iniciar sesión automáticamente.",
              "error"
            );
            navigate("/login");
            return;
          }

          const loginResult = await login(telefono, password, idNutriologo);

          if (loginResult && loginResult.success) {
            navigate("/welcomePage");
          } else {
            Swal.fire(
              "Error",
              "Error al iniciar sesión automáticamente. Por favor, inicia sesión manualmente.",
              "error"
            );
            navigate("/login");
          }
        }
      );
    } catch (error) {
      Swal.fire(
        "Error",
        "Error al crear el paciente. Por favor, intenta nuevamente.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFinalize = () => {
    const validationErrors = validatePaso6HablameDeTi();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setSnackbarMessage("Por favor, corrige los errores en el formulario.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    setErrors({});

    Swal.fire({
      title: "Confirmación",
      html: `
          Tus datos están a punto de enviarse, para que puedas ver tu dieta asignada, 
          se te va a crear una cuenta con el número de teléfono: 
          <p><span style="color: rgb(24,118,210,1); font-weight: bold;">
            ${formValuesBienvenida.telefono}
          </span></p>
          y la contraseña que pusiste al inicio.
      `,
      icon: "info",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, enviar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        handleSubmit();
      }
    });
  };

  return (
    // B) ENVOLVER TODO EN UN THEMEPROVIDER CON EL customTheme
    <ThemeProvider theme={customTheme}>
      <Container maxWidth="100%">
        <GlassPanel
          component="section"
          sx={{
            width: "100%",
            mt: 5,
            p: { xs: 3, sm: 4, md: 5 },
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 3,
          }}
        >
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            connector={<ColorlibConnector />}
            sx={{
              width: "100%",
              margin: "0 auto",
              py: 3,
              px: { xs: 1, sm: 2 },
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.6), transparent)",
              borderRadius: 3,
              [theme.breakpoints.down("sm")]: {
                gap: "2px",
                py: 2,
              },
            }}
          >
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel StepIconComponent={ColorlibStepIcon}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.85rem" },
                      fontWeight:
                        activeStep === index
                          ? 700
                          : activeStep > index
                          ? 600
                          : 500,
                      color:
                        activeStep === index
                          ? "primary.main"
                          : activeStep > index
                          ? "primary.dark"
                          : "text.secondary",
                      transition: "all 0.3s ease",
                      letterSpacing: 0.5,
                    }}
                  >
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mt: 0, mb: 2, width: "100%" }}>
            {renderStepContent(activeStep)}
          </Box>

          {activeStep !== 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={activeStep === 0}
                sx={{
                  borderRadius: 3,
                  py: 1.2,
                  px: 3,
                  fontWeight: 600,
                  borderColor: "rgba(148,163,184,0.3)",
                  color: "text.secondary",
                  backgroundColor: "rgba(255,255,255,0.8)",
                  backdropFilter: "blur(8px)",
                  boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(148,163,184,0.1)",
                    borderColor: "rgba(148,163,184,0.5)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 20px rgba(15,23,42,0.12)",
                  },
                  "&:disabled": {
                    opacity: 0.4,
                  },
                }}
              >
                Atrás
              </Button>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    borderRadius: 3,
                    py: 1.2,
                    px: 4,
                    fontWeight: 700,
                    background: (theme) =>
                      `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    color: "#fff",
                    boxShadow: (theme) =>
                      `0 12px 32px ${theme.palette.primary.main}40`,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: (theme) =>
                        `0 16px 40px ${theme.palette.primary.main}50`,
                    },
                  }}
                  onClick={handleFinalize}
                >
                  Finalizar
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  sx={{
                    borderRadius: 3,
                    py: 1.2,
                    px: 4,
                    fontWeight: 700,
                    background: (theme) =>
                      `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    color: "#fff",
                    boxShadow: (theme) =>
                      `0 12px 32px ${theme.palette.primary.main}40`,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: (theme) =>
                        `0 16px 40px ${theme.palette.primary.main}50`,
                    },
                  }}
                >
                  Siguiente
                </Button>
              )}
            </Box>
          )}
        </GlassPanel>
        {loading && <LinearProgress sx={{ width: "100%", marginTop: 2 }} />}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
};

export default PantallaRegistroPacienteOnline;
